/**
 * POT SKINS — cosmetic pot variants for the greenhouse, unlocked by earning the hand-written ACHIEVEMENT that
 * pays each one (`reward.unlocks: ["pot:<id>"]`, dealt by prestige — see sim/achievements.ts) or, for the
 * five first-generation skins, by claiming a named rung of the ACHIEVEMENT POINT TRACK (`reward.potSkin`,
 * derived by `skinsFromMilestones` — sim/pointTrack.ts). Applied PER POT via the save's `potSkins` map
 * (pot slotKey → skin id, see sim/save.ts).
 *
 * HARD RULE (see render/pixi/pot.ts): a skin may change the pot's BODY (the shape + palette BELOW the rim)
 * but must NOT change the TOP where the soil is — the rim band and the soil disc stay pixel-identical across
 * every skin, because a lot of the game keys off that geometry (POT_RIM_W is read by plant renderers to size
 * effects; POT_SOIL_OFFSET fixes each plant's origin; the moisture layer aligns to POT_SOIL_RX/RY). So a skin
 * carries a PALETTE + a BODY-SHAPE variant; `drawPot` draws the shared top and delegates the body to the skin.
 *
 * The default skin ("terracotta") reproduces the original pot exactly, so an un-set / unknown skin is safe.
 *
 * THIS FILE IS THE DATA HALF — ids, names, descriptions, palettes, the default. The BODY DRAWERS, the pot
 * ink and the shared body helpers live in render/pixi/potSkinArt.ts: sim/ never imports Pixi (the drawers'
 * FillGradient import, reached through collection.ts / cosmeticNames.ts, pulled Pixi into the landing page's
 * entry chunk — sim/simPurity.test.ts enforces the rule). A new skin is an entry here AND a body there;
 * sim/skinArt.test.ts fails if the two drift.
 */

/** The four recolourable pot tones. `soil` is the dirt-disc colour (usually left at the shared brown). */
export interface PotPalette {
  body: number;     // main body fill
  shade: number;    // shaded side of the body
  rim: number;      // the rim band
  soil: number;     // soil disc
}

/** A pot skin's DATA. Its body drawer is in render/pixi/potSkinArt.ts (`potSkinArt(id)` resolves both). */
export interface PotSkin {
  id: string;
  name: string;
  desc: string;         // one-liner shown in the picker
  palette: PotPalette;
}

// The skins' silhouettes, and the DISTINCTNESS notes that keep them apart, are in render/pixi/potSkinArt.ts.
export const POT_SKINS: PotSkin[] = [
  {
    id: "terracotta",
    name: "Terracotta",
    desc: "The classic clay pot. Free for everyone.",
    palette: { body: 0xc56a45, shade: 0x9b4f31, rim: 0xd98a63, soil: 0x4a3326 },
  },
  {
    id: "midnight",
    name: "Midnight Glaze",
    desc: "A deep black-glazed pot with a violet sheen.",
    palette: { body: 0x2a2740, shade: 0x161426, rim: 0x4a4570, soil: 0x2e2a22 },
  },
  {
    id: "urn",
    name: "Round Urn",
    desc: "A round urn that swells at the middle.",
    palette: { body: 0xb98a4e, shade: 0x8a6132, rim: 0xd4a86a, soil: 0x4a3326 },
  },
  {
    id: "pedestal",
    name: "Stone Pedestal",
    desc: "A footed stone planter on a wide base.",
    palette: { body: 0x8f9186, shade: 0x63655c, rim: 0xa9ab9f, soil: 0x3e3a30 },
  },
  {
    id: "porcelain",
    name: "Blue Porcelain",
    desc: "A glossy tapered pot in willow-pattern blue.",
    palette: { body: 0xe8ecf4, shade: 0xb9c4d8, rim: 0x4a6fb0, soil: 0x3e3a30 },
  },
  {
    id: "mossy",
    name: "Mossy Stone",
    desc: "A weathered stone pot dusted with moss.",
    palette: { body: 0x6f7a5e, shade: 0x4c543f, rim: 0x828d6c, soil: 0x3a3728 },
  },

  // ---- second generation: each its own silhouette (profile bodies, above) ------------------------------
  // Half-widths just under the rim (t = 0.3), at mid-height (0.6) and at the base, for telling them apart:
  //   verdigris 36.6/36.1/35.5 + two 2.4 px bands  — the only VERTICAL wall
  //   blackGold 41/46/17 on a 5 px foot ring        — widest LOW (the urn is widest HIGH, just under its rim)
  //   loam      31.7/26.9/20 + ridges, stepped foot — the narrowest waist of any skin, a pinched neck
  //   ember     33/32/43                            — the only body wider at the FOOT than at the rim
  //   gilt      35/16/27 through a 7 px stem        — the only one with a stem
  //   ghostlight 42.5/37/24, corner at 0.42         — a shoulder that steps OUT, flat planes
  //   prism     37/32/10, corner at 0.46            — a gem's point: the narrowest base by 10 px
  // Every max half-width stays ≤ 46 (the urn reaches 43), so no body reaches the next pot on a shelf.
  {
    id: "verdigris",
    name: "Verdigris Copper",
    desc: "Riveted copper, polished where hands touch it and green everywhere else.",
    palette: { body: 0xc47a46, shade: 0x86462a, rim: 0x5f9c84, soil: 0x3e3226 },
  },
  {
    id: "blackGold",
    name: "Black Gold",
    desc: "A black-glazed bowl, broken once and mended in gold.",
    palette: { body: 0x33302e, shade: 0x181615, rim: 0x55504a, soil: 0x2e2a22 },
  },
  {
    id: "loam",
    name: "Loam Stoneware",
    desc: "Speckled stoneware with a bare base.",
    palette: { body: 0x8e6b48, shade: 0x5c412a, rim: 0xa27f58, soil: 0x3e3226 },
  },
  {
    id: "ghostlight",
    name: "Ghostlight Crackle",
    desc: "A faceted pot in crackled teal glaze with a cold glow.",
    palette: { body: 0x2b4d45, shade: 0x163330, rim: 0x3a6258, soil: 0x2e2a22 },
  },
  {
    id: "ember",
    name: "Ember Glaze",
    desc: "Burnt-orange glaze running down to a charred base.",
    palette: { body: 0xc4602e, shade: 0x7e3016, rim: 0xd2743e, soil: 0x3a2a20 },
  },
  {
    id: "foundersGilt",
    name: "Founder's Gilt",
    desc: "A footed chalice in green-black enamel, banded in gold.",
    palette: { body: 0x2c4540, shade: 0x13211f, rim: 0xc2922e, soil: 0x2e2a22 },
  },
  {
    // the Kickstarter backer pot: the Founder's Gilt chalice in violet-black enamel, every gilt surface
    // anodized (render/pixi/potSkinArt.ts ANODIZED METAL). `rim` stays the gilt's gold — the anodize is
    // computed FROM it, exactly as the mockup recoloured the gilt render.
    id: "voidAnodized",
    name: "Void Anodized",
    desc: "The founder's chalice in deep violet enamel with rainbow metal.",
    palette: { body: 0x2a2342, shade: 0x120e1f, rim: 0xc2922e, soil: 0x2e2a22 },
  },
  {
    id: "prism",
    name: "Prism",
    desc: "A cut crystal with facets that shift from green to cyan to violet.",
    palette: { body: 0x7cc8a8, shade: 0x4c4488, rim: 0x74bdb4, soil: 0x2e2a22 },
  },
];

const SKIN_BY_ID = new Map(POT_SKINS.map((s) => [s.id, s]));

/** The default skin id (the original terracotta) — used when none is selected/unlocked. */
export const DEFAULT_POT_SKIN = "terracotta";

/** Resolve a skin id to its definition, falling back to the default for unknown/absent ids. */
export function potSkin(id: string | undefined): PotSkin {
  return (id && SKIN_BY_ID.get(id)) || SKIN_BY_ID.get(DEFAULT_POT_SKIN)!;
}
